/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box, Button, SvgIcon } from '@material-ui/core';
import { Dispatch } from '@reduxjs/toolkit';
import CustomField from 'components/Form/Input/CustomField';
import DraggableSelect from 'components/Form/Input/DraggableSelect/DraggableSelect';
import { CustomTypography } from 'components/Form/Input/Input.styled';
import { FormApi } from 'final-form';
import Clear from 'icons/Clear';
import React, { ChangeEvent, Component, MouseEvent } from 'react';
import { Field, Form } from 'react-final-form';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AppDispatch, AppState } from 'store';
import { setProjectSearch, setProjectsPagination } from 'store/projects/projects.actions';
import { IProjectState, IQueuePagination } from 'store/projects/projects.interface';
import {
  getProjectLanguagesSelector,
  getProjectSearchSelector,
  getProjectsSelector,
  getProjectWorkflowStepsSelector,
  getXTMCustomersForCurrentUserSelector,
  getXTMRequestersForProjectSelector,
} from 'store/projects/projects.selectors';
import { IAutocompleteField } from 'types/shared';
import { isNumber } from 'utils/customValidators';
import { KontentProjectLanguageMappingDTO, XTMJobStatus, XTMWorkflowStepDTO } from 'utils/restApplicationClient';
import {
  Arrow,
  ButtonsContainer,
  CustomSlider,
  FilterMenuButton,
  FilterMenuContainer,
  FilterMenuFilterForm,
  FilterMenuPopper,
  FilterMenuProgressContainer,
  FilterMenuRowContainer,
} from './FilterMenu.styled';
import RangeDatepicker from './RangeDatepicker/RangeDatepicker';

interface IProps {
  disabled?: boolean;
}
interface IStateProps {
  projectsState: IProjectState;
  projectLanguages: Array<KontentProjectLanguageMappingDTO>;
  projectWorkflowSteps: Array<XTMWorkflowStepDTO>;
  xtmRequesters: Array<IAutocompleteField>;
  xtmCustomers: Array<IAutocompleteField>;
  projectSearch: string;
}

interface IDispatchProps {
  setProjectPagination: (payload: IQueuePagination) => AppDispatch;
  setProjectSearch: (payload: string) => AppDispatch;
}

interface IState {
  anchorEl: Element | null;
  progress: number[];
  createdDateFrom?: Date;
  createdDateTo?: Date;
  dueDateFrom?: Date;
  dueDateTo?: Date;
  projectName: string;
  projectNameError?: string;
  projectNameTouched: boolean;
}

interface IFilterForm {
  [key: string]: string | string[] | IAutocompleteField[] | undefined;
  projectId?: string;
  fileName?: string;
  jobId?: string;
  sourceLanguages?: IAutocompleteField[];
  targetLanguages?: IAutocompleteField[];
  requesters?: IAutocompleteField[];
  customers?: IAutocompleteField[];
  statuses?: IAutocompleteField[];
  workflowSteps?: IAutocompleteField[];
}

type PropType = WithTranslation & IDispatchProps & IStateProps & IProps;

class FilterMenu extends Component<PropType, IState> {
  private statuses: Array<IAutocompleteField<XTMJobStatus>>;

  constructor(props: PropType) {
    super(props);

    const {
      projectsState: { pagination },
      projectSearch,
    } = this.props;

    const { createdDateFrom, createdDateTo, dueDateFrom, dueDateTo, progressFrom, progressTo } = pagination;

    this.state = {
      anchorEl: null,
      progress: [progressFrom || 0, progressTo || 100],
      createdDateFrom,
      createdDateTo,
      dueDateFrom,
      dueDateTo,
      projectName: projectSearch,
      projectNameTouched: false,
    };
    this.statuses = this.getStatuses();
  }

  componentDidUpdate({ projectSearch: previousSearch }: PropType, { projectName: previousName }: IState): void {
    const { projectName } = this.state;
    const { projectSearch } = this.props;

    if (previousSearch !== projectSearch && (projectSearch !== projectName || previousName === projectName)) {
      this.setState({ projectName: projectSearch });
    }
  }

  handleClick = (event: MouseEvent): void => {
    const { anchorEl } = this.state;
    this.setState({ anchorEl: anchorEl ? null : event.currentTarget });
  };

  handleClose = (): void => {
    this.setState({ anchorEl: null });
  };

  handleProgressChange = (event: ChangeEvent<{}>, value: number | number[]): void => {
    this.setState({ progress: value as number[] });
  };

  clearProgress = (event: MouseEvent<HTMLElement>): void => {
    this.setState({ progress: [0, 100] });
  };

  handleSentDateChange = (createdDateFrom?: Date, createdDateTo?: Date): void => {
    this.setState({ createdDateFrom, createdDateTo });
  };

  handleDueDateChange = (dueDateFrom?: Date, dueDateTo?: Date): void => {
    this.setState({ dueDateFrom, dueDateTo });
  };

  parseFormFields = (formValues: IFilterForm): IQueuePagination => {
    const { progress, createdDateFrom, createdDateTo, dueDateFrom, dueDateTo } = this.state;
    const {
      projectsState: {
        pagination: { order, orderBy, page, size },
      },
    } = this.props;

    return {
      order,
      orderBy,
      page,
      size,
      ...formValues,
      workflowSteps: formValues.workflowSteps,
      statuses: formValues.statuses?.map(({ value }) => value as XTMJobStatus),
      sourceLanguages: formValues.sourceLanguages,
      targetLanguages: formValues.targetLanguages,
      createdDateFrom,
      createdDateTo,
      dueDateFrom,
      dueDateTo,
      progressFrom: progress[0] !== 0 ? progress[0] : undefined,
      progressTo: progress[1] !== 100 ? progress[1] : undefined,
    };
  };

  handleReset = (form: FormApi<IFilterForm>) => (): void => {
    const {
      setProjectPagination,
      projectsState: {
        pagination: { order, orderBy, page, size, imported },
      },
      setProjectSearch,
    } = this.props;
    form.reset();
    this.setState({
      createdDateFrom: undefined,
      createdDateTo: undefined,
      dueDateFrom: undefined,
      dueDateTo: undefined,
      progress: [0, 100],
      projectName: '',
      projectNameError: undefined,
    });
    setProjectPagination({
      order,
      orderBy,
      page,
      size,
      imported,
      createdDateFrom: undefined,
      createdDateTo: undefined,
      dueDateFrom: undefined,
      dueDateTo: undefined,
      fileName: undefined,
      jobId: undefined,
      progressFrom: undefined,
      progressTo: undefined,
      projectId: undefined,
      sourceLanguages: undefined,
      statuses: undefined,
      targetLanguages: undefined,
      workflowSteps: undefined,
      customers: undefined,
      requesters: undefined,
    });
    setProjectSearch('');

    this.handleClose();
  };

  onSubmit = (values: IFilterForm): void => {
    const { setProjectPagination, setProjectSearch } = this.props;
    const { projectName, projectNameTouched, projectNameError } = this.state;

    if (!projectNameError) {
      setProjectPagination(this.parseFormFields(values));
      setProjectSearch(projectName);
      this.handleClose();
    }
    if (!projectNameTouched) {
      this.setState({ projectNameTouched: true });
    }
  };

  handleProjectNameChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
    const { projectNameTouched } = this.state;
    let error;

    if (projectNameTouched && value.length < 3 && value.length > 0) {
      error = 'errormessage.general.searcherror';
    }

    this.setState({ projectName: value, projectNameError: error });
  };

  handleProjectNameBlur = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
    let error;

    if (value.length < 3 && value.length > 0) {
      error = 'errormessage.general.searcherror';
    }

    this.setState({ projectNameTouched: true, projectNameError: error });
  };

  getStatuses = (): Array<IAutocompleteField<XTMJobStatus>> => {
    const { t } = this.props;
    const statuses: Array<XTMJobStatus> = ['NEW', 'SENT', 'IN_PROGRESS', 'FINISHED', 'CANCELLED'];

    return statuses.map((status) => ({
      value: status,
      label: t(`queue.status.${status}`),
    }));
  };

  parseWorkflowSteps = (): Array<IAutocompleteField<string>> => {
    const { projectWorkflowSteps, t } = this.props;

    return projectWorkflowSteps.map(({ step }) => ({
      value: step,
      label: step || t('queue.workflowSteps.null'),
    }));
  };

  parseLanguages = (): Array<IAutocompleteField<string>> => {
    const { projectLanguages } = this.props;

    return projectLanguages.map(({ kontentLanguageName, kontentLanguageCode }) => ({
      label: kontentLanguageName,
      value: kontentLanguageCode,
    }));
  };

  render(): JSX.Element {
    const {
      anchorEl,
      progress,
      createdDateFrom,
      createdDateTo,
      dueDateFrom,
      dueDateTo,
      projectName,
      projectNameTouched,
      projectNameError,
    } = this.state;
    const {
      t,
      disabled,
      projectsState: {
        isFiltered,
        pagination: {
          order,
          orderBy,
          page,
          size,
          imported,
          sourceLanguages,
          targetLanguages,
          workflowSteps,
          customers,
          requesters,
          statuses: statusesProperty,
          ...rest
        },
        xtmCustomers,
        xtmRequesters,
      },
    } = this.props;
    const initialStatuses = statusesProperty
      ?.map((status) =>
        this.statuses.find(({ value }) => {
          return status === value;
        })
      )
      .filter((value) => value);
    const languageOptions = this.parseLanguages();
    const workflowStepsOptions = this.parseWorkflowSteps();

    return (
      <div>
        <FilterMenuButton
          onClick={this.handleClick}
          opened={Number(!!anchorEl)}
          filtered={Number(isFiltered && !imported)}
          disabled={disabled}
          startIcon={
            <SvgIcon>
              <path d="M4.25,5.61C6.27,8.2,10,13,10,13v6c0,0.55,0.45,1,1,1h2c0.55,0,1-0.45,1-1v-6c0,0,3.72-4.8,5.74-7.39 C20.25,4.95,19.78,4,18.95,4H5.04C4.21,4,3.74,4.95,4.25,5.61z" />
            </SvgIcon>
          }
        >
          <Box fontSize="14px" fontWeight="500">
            {t('common.filter')}
          </Box>
          {!!anchorEl && <Arrow />}
        </FilterMenuButton>
        <FilterMenuPopper open={!!anchorEl} anchorEl={anchorEl} placement="bottom-start" disablePortal={true}>
          <FilterMenuContainer elevation={4}>
            <Form
              onSubmit={this.onSubmit}
              subscription={{ pristine: true, submitting: true }}
              initialValues={{
                ...rest,
              }}
              render={({ handleSubmit, form }): JSX.Element => (
                <FilterMenuFilterForm onSubmit={handleSubmit}>
                  <FilterMenuRowContainer>
                    <CustomField
                      label="queue.filters.projectName"
                      input={
                        {
                          onChange: this.handleProjectNameChange,
                          onBlur: this.handleProjectNameBlur,
                          value: projectName,
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } as any
                      }
                      meta={{ error: projectNameError, touched: projectNameTouched }}
                    />

                    <Field
                      name="projectId"
                      component={CustomField}
                      validate={isNumber}
                      label="queue.filters.projectId"
                    />
                  </FilterMenuRowContainer>

                  <FilterMenuRowContainer>
                    <Field name="fileName" component={CustomField} label="queue.filters.fileName" />

                    <Field name="jobId" component={CustomField} validate={isNumber} label="queue.filters.jobId" />
                  </FilterMenuRowContainer>

                  <FilterMenuRowContainer>
                    <Field
                      name="sourceLanguages"
                      component={DraggableSelect}
                      label={'queue.filters.sourceLanguage'}
                      initialValue={sourceLanguages}
                      options={languageOptions}
                    />

                    <Field
                      name="targetLanguages"
                      component={DraggableSelect}
                      label={'queue.filters.targetLanguage'}
                      initialValue={targetLanguages}
                      options={languageOptions}
                    />
                  </FilterMenuRowContainer>

                  <FilterMenuRowContainer>
                    <Field
                      name="customers"
                      component={DraggableSelect}
                      initialValue={customers}
                      label={'queue.filters.customers'}
                      options={xtmCustomers}
                    />

                    <Field
                      name="requesters"
                      component={DraggableSelect}
                      initialValue={requesters}
                      label={'queue.filters.requesters'}
                      options={xtmRequesters}
                    />
                  </FilterMenuRowContainer>

                  <FilterMenuRowContainer>
                    <RangeDatepicker
                      dateFrom={createdDateFrom}
                      dateTo={createdDateTo}
                      onChange={this.handleSentDateChange}
                      label={'sentDate'}
                    />

                    <RangeDatepicker
                      dateFrom={dueDateFrom}
                      dateTo={dueDateTo}
                      onChange={this.handleDueDateChange}
                      label={'dueDate'}
                    />
                  </FilterMenuRowContainer>

                  <FilterMenuRowContainer>
                    <Field
                      name="workflowSteps"
                      component={DraggableSelect}
                      initialValue={workflowSteps}
                      label={'queue.filters.workflowStep'}
                      options={workflowStepsOptions}
                    />

                    <Field
                      name="statuses"
                      component={DraggableSelect}
                      initialValue={initialStatuses}
                      label={'queue.filters.status'}
                      options={this.statuses}
                    />
                  </FilterMenuRowContainer>

                  <FilterMenuRowContainer>
                    <FilterMenuProgressContainer>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="baseline"
                        style={{
                          cursor: 'pointer',
                        }}
                      >
                        <CustomTypography color="textSecondary" variant="subtitle2">
                          {t('queue.filters.progress')}
                        </CustomTypography>
                        <Box
                          fontSize="11px"
                          color="#FF6347"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="baseline"
                          onClick={this.clearProgress}
                        >
                          {t('queue.filters.clear')}
                          <Clear />
                        </Box>
                      </Box>

                      <CustomSlider value={progress} onChange={this.handleProgressChange} valueLabelDisplay="on" />
                    </FilterMenuProgressContainer>
                  </FilterMenuRowContainer>

                  <FilterMenuRowContainer>
                    <ButtonsContainer item xs={12}>
                      <Button type="submit" size="medium" variant="contained" color="secondary">
                        <Box fontWeight="fontWeightBold">{t('queue.filters.apply')}</Box>
                      </Button>
                      <Button size="medium" variant="outlined" color="primary" onClick={this.handleReset(form)}>
                        <Box fontWeight="fontWeightBold">{t('common.reset')}</Box>
                      </Button>
                    </ButtonsContainer>
                  </FilterMenuRowContainer>
                </FilterMenuFilterForm>
              )}
            />
          </FilterMenuContainer>
        </FilterMenuPopper>
      </div>
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  projectsState: getProjectsSelector(state),
  projectLanguages: getProjectLanguagesSelector(state),
  projectWorkflowSteps: getProjectWorkflowStepsSelector(state),
  xtmCustomers: getXTMCustomersForCurrentUserSelector(state),
  xtmRequesters: getXTMRequestersForProjectSelector(state),
  projectSearch: getProjectSearchSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  setProjectPagination: (payload: IQueuePagination): AppDispatch => dispatch(setProjectsPagination(payload)),
  setProjectSearch: (payload: string): AppDispatch => dispatch(setProjectSearch(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(FilterMenu));
